import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Button,
} from "@mui/material";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import axios from "axios";
import { REACT_APP_API_BASE_URL } from "../../config";
import ProductModal from "../product_modal";
import { ImagePreviewDialog, ClickableImage } from "./ImagePreview";
import { ProductsTableHeader } from "./TableComponents";
import { ActionButtons } from "./ActionButtons";

const FoundProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSku, setSelectedSku] = useState(null);
  const [imageUrls, setImageUrls] = useState({}); // { image_url: url }
  const [mainImageUrls, setMainImageUrls] = useState({}); // { sku: main_image_url }
  const [authUser, setAuthUser] = useState(undefined); // undefined: loading, null: not logged in, object: user
  const [processingRows, setProcessingRows] = useState({
    tick: null,
    cross: null,
  }); // Track which row is being processed

  // Wait for Firebase auth state before fetching
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthUser(user || null);
    });
    return () => unsubscribe();
  }, []);

  // Fetch products only when user is available
  useEffect(() => {
    if (authUser === undefined) return; // Wait for auth
    if (!authUser) {
      setLoading(false);
      setError("User not authenticated");
      return;
    }
    const fetchProducts = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await authUser.getIdToken();
        const res = await axios.get(`${REACT_APP_API_BASE_URL}/report/found`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // API returns { status, data: [...] }
        const arr = Array.isArray(res.data?.data) ? res.data.data : [];
        setProducts(arr);
      } catch (err) {
        setError(err.message || "Failed to fetch found products");
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [authUser]);

  // Fetch all image URLs on products load
  useEffect(() => {
    const fetchAllImageUrls = async () => {
      if (!products.length) return;
      const user = getAuth().currentUser;
      if (!user) return;
      const urls = {};
      products.forEach((row) => {
        if (row.image_url) {
          urls[row.image_url] = row.image_url;
        }
      });
      setImageUrls(urls);
    };
    fetchAllImageUrls();
  }, [products]);

  // Fetch all main image URLs on products load
  useEffect(() => {
    const fetchAllMainImageUrls = async () => {
      if (!products.length) return;
      const user = getAuth().currentUser;
      if (!user) return;
      const token = await user.getIdToken();
      const headers = { Authorization: `Bearer ${token}` };
      const urls = {};
      await Promise.all(
        products.map(async (row) => {
          if (row.sku) {
            try {
              const res = await axios.get(
                `${REACT_APP_API_BASE_URL}/products/${row.sku}/`,
                { headers }
              );
              if (
                res.data &&
                res.data.main_image &&
                res.data.main_image.image_url
              ) {
                urls[row.sku] = res.data.main_image.image_url;
              }
            } catch (e) {
              urls[row.sku] = null;
            }
          }
        })
      );
      setMainImageUrls(urls);
    };
    fetchAllMainImageUrls();
  }, [products]);

  // Handle tick click to upload image and remove row
  const handleTickClick = async (imageKey, sku, rowId) => {
    try {
      setProcessingRows((prev) => ({ ...prev, tick: rowId }));
      const user = getAuth().currentUser;
      if (!user) throw new Error("User not authenticated");
      const token = await user.getIdToken();
      const payload = {
        image_url: imageKey,
        sku: sku,
      };
      await axios.post(`${REACT_APP_API_BASE_URL}/report/addImage`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts((prev) => prev.filter((row) => row.id !== rowId));
    } catch (err) {
      setError(err.message || "Failed to upload image");
    } finally {
      setProcessingRows((prev) => ({ ...prev, tick: null }));
    }
  };

  // Handle cross click to delete report and remove row
  const handleCrossClick = async (imageKey, sku, rowId) => {
    try {
      setProcessingRows((prev) => ({ ...prev, cross: rowId }));
      const user = getAuth().currentUser;
      if (!user) throw new Error("User not authenticated");
      const token = await user.getIdToken();
      const payload = {
        image_url: imageKey,
        sku: sku,
      };
      await axios.post(`${REACT_APP_API_BASE_URL}/report/delReport`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts((prev) => prev.filter((row) => row.id !== rowId));
    } catch (err) {
      setError(err.message || "Failed to delete report");
    } finally {
      setProcessingRows((prev) => ({ ...prev, cross: null }));
    }
  };

  // Handle image click to fetch and preview the image
  const handleImageClick = async (imageKey) => {
    try {
      setPreviewImage(imageKey);
      setPreviewOpen(true);
    } catch (err) {
      setError("Failed to load image");
    }
  };

  // Helper to format date time from epoch microseconds
  const formatDateTime = (epochMicroseconds) => {
    if (!epochMicroseconds) return "";
    const d = new Date(parseInt(epochMicroseconds) / 1000);
    if (isNaN(d)) return epochMicroseconds;
    const pad = (n) => n.toString().padStart(2, "0");
    const yyyy = d.getUTCFullYear();
    const mm = pad(d.getUTCMonth() + 1);
    const dd = pad(d.getUTCDate());
    const hh = pad(d.getUTCHours());
    const min = pad(d.getUTCMinutes());
    const ss = pad(d.getUTCSeconds());
    return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{ flexGrow: 1, fontWeight: "bold" }}
        >
          Found Reports
        </Typography>
      </Box>
      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size="small">
            <ProductsTableHeader type="found" />
            <TableBody>
              {products.map((row, idx) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Button
                      variant="text"
                      sx={{
                        color: "primary.main",
                        textTransform: "none",
                        fontWeight: 600,
                      }}
                      onClick={() => {
                        setSelectedSku(row.sku);
                        setModalOpen(true);
                      }}
                    >
                      {row.sku}
                    </Button>
                  </TableCell>
                  <TableCell>{row.user_email}</TableCell>
                  <TableCell>{row.rank}</TableCell>
                  <TableCell>{formatDateTime(row.created_time)}</TableCell>
                  <TableCell>
                    {row.sku &&
                      (mainImageUrls[row.sku] ? (
                        <ClickableImage
                          src={mainImageUrls[row.sku]}
                          alt="Main"
                          onClick={() => {
                            setPreviewImage(mainImageUrls[row.sku]);
                            setPreviewOpen(true);
                          }}
                        />
                      ) : (
                        <Typography color="error" variant="body2">
                          Image not found
                        </Typography>
                      ))}
                  </TableCell>
                  <TableCell>
                    {row.image_url &&
                      (imageUrls[row.image_url] ? (
                        <ClickableImage
                          src={imageUrls[row.image_url]}
                          alt="Preview"
                          onClick={() => handleImageClick(row.image_url)}
                        />
                      ) : (
                        <Typography color="error" variant="body2">
                          Image not found
                        </Typography>
                      ))}
                  </TableCell>
                  <ActionButtons
                    rowId={row.id}
                    imageKey={row.image_url}
                    sku={row.sku}
                    processingRows={processingRows}
                    onTickClick={handleTickClick}
                    onCrossClick={handleCrossClick}
                  />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}{" "}
      {/* Image preview dialog */}
      <ImagePreviewDialog
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewImage(null);
        }}
        imageUrl={previewImage}
      />
      {/* Product modal */}
      {modalOpen && selectedSku && (
        <ProductModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedSku(null);
          }}
          sku={selectedSku}
          hideReportFound={true}
        />
      )}
    </Box>
  );
};

export default FoundProducts;
